import React, { useState, useEffect } from 'react';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave,
  FaEye,
  FaFileAlt,
  FaQuestionCircle,
  FaShieldAlt,
  FaBell
} from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import './ContentManagement.css';

const ContentManagement = () => {
  const [activeTab, setActiveTab] = useState('faqs');
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const [faqs, setFaqs] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [articles, setArticles] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [faqsRes, policiesRes, articlesRes, templatesRes] = await Promise.all([
        api.get(ENDPOINTS.CONTENT.FAQS),
        api.get(ENDPOINTS.CONTENT.POLICIES),
        api.get(ENDPOINTS.CONTENT.HELP),
        api.get(ENDPOINTS.CONTENT.TEMPLATES)
      ]);
      setFaqs(faqsRes.data || faqsRes);
      setPolicies(policiesRes.data || policiesRes);
      setArticles(articlesRes.data || articlesRes);
      setTemplates(templatesRes.data || templatesRes);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'faqs', label: 'FAQs', icon: <FaQuestionCircle /> },
    { id: 'policies', label: 'Terms & Policies', icon: <FaShieldAlt /> },
   // { id: 'articles', label: 'Help Articles', icon: <FaFileAlt /> },
  //  { id: 'templates', label: 'Notification Templates', icon: <FaBell /> }
  ];

  const handleDeleteFAQ = async (id) => {
    if (window.confirm('Are you sure you want to delete this FAQ?')) {
      try {
        await api.delete(`${ENDPOINTS.CONTENT.FAQS}/${id}`);
        setFaqs(faqs.filter(faq => faq.id !== id));
      } catch (error) {
        toast.error('Failed to delete FAQ');
      }
    }
  };

  const handleDeletePolicy = async (id) => {
    if (window.confirm('Are you sure you want to delete this policy?')) {
      try {
        await api.delete(`${ENDPOINTS.CONTENT.POLICIES}/${id}`);
        setPolicies(policies.filter(p => p.id !== id));
      } catch (error) {
        toast.error('Failed to delete policy');
      }
    }
  };

  const handleDeleteArticle = async (id) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await api.delete(`${ENDPOINTS.CONTENT.HELP}/${id}`);
        setArticles(articles.filter(article => article.id !== id));
      } catch (error) {
        toast.error('Failed to delete article');
      }
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await api.delete(`${ENDPOINTS.CONTENT.TEMPLATES}/${id}`);
        setTemplates(templates.filter(t => t.id !== id));
      } catch (error) {
        toast.error('Failed to delete template');
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const type = editingItem.type;
    const isEdit = !!editingItem.id;
    const url = ENDPOINTS.CONTENT[type.toUpperCase().replace('ARTICLES', 'HELP')];
    
    // Sanitize data: remove non-whitelisted fields for backend
    const sanitizedData = { ...editingItem.data };
    
    // Auto-clean content: replace common PDF/Word junk symbols with cleaner bullets
    const cleanContent = (text) => {
      if (!text) return text;
      // If it looks like a full HTML document, don't clean excessive whitespace as it breaks CSS/Layout
      const isFullHtml = text.trim().toLowerCase().startsWith('<!doctype') || text.toLowerCase().includes('<html');
      
      let cleaned = text.replace(/[☐☑☒]/g, '• ').replace(/\u00A0/g, ' ');
      
      if (!isFullHtml) {
        // Standard cleaning: collapse multiple spaces but PRESERVE newlines/paragraphs
        cleaned = cleaned.replace(/[^\S\r\n]{2,}/g, ' '); 
      }
      return cleaned;
    };

    if (sanitizedData.answer) sanitizedData.answer = cleanContent(sanitizedData.answer);
    if (sanitizedData.content) sanitizedData.content = cleanContent(sanitizedData.content);
    if (sanitizedData.body) sanitizedData.body = cleanContent(sanitizedData.body);

    delete sanitizedData.id;
    delete sanitizedData.createdAt;
    delete sanitizedData.updatedAt;
    delete sanitizedData.views;

    // For templates, ensure variables is an array
    if (type === 'templates' && typeof sanitizedData.variables === 'string') {
      try {
        sanitizedData.variables = JSON.parse(sanitizedData.variables);
      } catch (e) {
        sanitizedData.variables = sanitizedData.variables.split(',').map(v => v.trim());
      }
    }
    
    try {
      if (isEdit) {
        await api.patch(`${url}/${editingItem.id}`, sanitizedData);
      } else {
        await api.post(url, sanitizedData);
      }
      setEditingItem(null);
      fetchData();
      toast.success(`Content ${isEdit ? 'updated' : 'created'} successfully!`);
    } catch (error) {
      console.error('Failed to save content:', error);
      toast.error('Failed to save content');
    }
  };

  const openAddModal = (type) => {
    const defaultData = {
      faqs: { question: '', answer: '', category: 'General', displayOrder: 1, isActive: true },
      policies: { title: '', type: 'terms_of_service', version: '1.0', content: '', effectiveDate: new Date().toISOString().split('T')[0], isActive: true },
      articles: { title: '', slug: '', category: 'General', content: '', isPublished: true },
      templates: { name: '', type: 'email', subject: '', body: '', variables: [], isActive: true }
    };
    setEditingItem({ type, data: defaultData[type] });
  };

  const openEditModal = (type, item) => {
    setEditingItem({ type, id: item.id, data: { ...item } });
  };

  if (loading) return <div className="loading">Loading content...</div>;

  return (
    <div className="content-management-page">
      <div className="page-header">
        <div>
          <h1>Content Management</h1>
          <p className="page-subtitle">Manage FAQs, policies, articles, and notification templates</p>
        </div>
        {/* <div className="page-actions">
          <button className="btn btn-primary" onClick={() => openAddModal(activeTab)}>
            <FaPlus /> Add {activeTab === 'faqs' ? 'FAQ' : activeTab === 'policies' ? 'Policy' : activeTab === 'articles' ? 'Article' : 'Template'}
          </button>
        </div> */}
      </div>

      {/* Content Tabs */}
      <div className="content-tabs-refined">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`content-tab-btn-refined ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="content-container-refined">
        <div className="content-area-refined">

          {/* FAQs TAB */}
          {activeTab === 'faqs' && (
            <div className="content-section">
              <div className="section-header">
                <h2>Frequently Asked Questions</h2>
                <button className="btn btn-primary btn-sm" onClick={() => openAddModal('faqs')}>
                  <FaPlus /> Add FAQ
                </button>
              </div>

              <div className="faqs-list">
                {faqs.map((faq) => (
                  <div key={faq.id} className="faq-card">
                    <div className="faq-header">
                      <div>
                        <h4 className="faq-question">{faq.question}</h4>
                        <div className="faq-category-badge">{faq.category}</div>
                      </div>
                      <div className="faq-actions">
                        <button className="btn-icon" title="View Preview" onClick={() => setViewingItem({ type: 'FAQ', title: faq.question, content: faq.answer })}>
                          <FaEye />
                        </button>
                        <button className="btn-icon" title="Edit" onClick={() => openEditModal('faqs', faq)}>
                          <FaEdit />
                        </button>
                        <button 
                          className="btn-icon delete" 
                          title="Delete"
                          onClick={() => handleDeleteFAQ(faq.id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <div className="faq-body">
                      <div className="faq-answer-markdown">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]} 
                          rehypePlugins={[rehypeRaw]}
                        >
                          {faq.answer}
                        </ReactMarkdown>
                      </div>
                    </div>
                    <div className="faq-footer">
                      <span className={`status-badge ${faq.isActive ? 'active' : 'inactive'}`}>
                        {faq.isActive ? 'Published' : 'Draft'}
                      </span>
                      <span className="order-badge">Order: {faq.displayOrder}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* POLICIES TAB */}
          {activeTab === 'policies' && (
            <div className="content-section">
              <div className="section-header">
                <h2>Terms & Privacy Policies</h2>
                <button className="btn btn-primary btn-sm" onClick={() => openAddModal('policies')}>
                  <FaPlus /> Add Policy
                </button>
              </div>

              <div className="policies-list">
                {policies.map((policy) => (
                  <div key={policy.id} className="policy-card">
                    <div className="policy-header">
                      <div>
                        <h3>{policy.title || policy.type}</h3>
                        <span className="version-badge">v{policy.version}</span>
                      </div>
                      <div className="policy-actions">
                        <button className="btn-icon" title="Edit" onClick={() => openEditModal('policies', policy)}>
                          <FaEdit />
                        </button>
                        <button 
                          className="btn-icon delete" 
                          title="Delete"
                          onClick={() => handleDeletePolicy(policy.id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                      <div className="markdown-excerpt">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]} 
                          rehypePlugins={[rehypeRaw]}
                        >
                          {policy.content.substring(0, 150) + '...'}
                        </ReactMarkdown>
                      </div>
                    <div className="policy-footer">
                      <div className="policy-dates">
                        <span>Effective: {new Date(policy.effectiveDate).toLocaleDateString()}</span>
                      </div>
                      <span className={`status-badge ${policy.isActive ? 'active' : 'inactive'}`}>
                        {policy.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <button 
                        className="btn-icon view-btn" 
                        title="View Full Content"
                        onClick={() => setViewingItem({ type: 'Policy', title: policy.title || policy.type, content: policy.content })}
                      >
                        <FaEye />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ARTICLES TAB */}
          {activeTab === 'articles' && (
            <div className="content-section">
              <div className="section-header">
                <h2>Help Articles</h2>
                <button className="btn btn-primary btn-sm" onClick={() => openAddModal('articles')}>
                  <FaPlus /> Create Article
                </button>
              </div>

              <div className="articles-grid">
                {articles.map((article) => (
                  <div key={article.id} className="article-card">
                    <div className="article-category">{article.category}</div>
                    <h3 className="article-title">{article.title}</h3>
                    <div className="markdown-excerpt">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]} 
                        rehypePlugins={[rehypeRaw]}
                      >
                        {article.content.substring(0, 100) + '...'}
                      </ReactMarkdown>
                    </div>
                    <div className="article-meta">
                      <span className="views-count">
                        <FaEye /> {article.views} views
                      </span>
                      <span className="article-date">{new Date(article.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="article-footer">
                      <span className={`status-badge ${article.isPublished ? 'active' : 'inactive'}`}>
                        {article.isPublished ? 'Published' : 'Draft'}
                      </span>
                      <div className="article-actions">
                        <button className="btn-icon" title="Edit" onClick={() => openEditModal('articles', article)}>
                          <FaEdit />
                        </button>
                        <button 
                          className="btn-icon delete" 
                          title="Delete"
                          onClick={() => handleDeleteArticle(article.id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TEMPLATES TAB */}
          {activeTab === 'templates' && (
            <div className="content-section">
              <div className="section-header">
                <h2>Notification Templates</h2>
                <button className="btn btn-primary btn-sm" onClick={() => openAddModal('templates')}>
                  <FaPlus /> Create Template
                </button>
              </div>

              <div className="templates-list">
                {templates.map((template) => (
                  <div key={template.id} className="template-card">
                    <div className="template-header">
                      <div>
                        <h3>{template.name}</h3>
                        <span className="type-badge">{template.type}</span>
                      </div>
                      <div className="template-actions">
                        <button className="btn-icon" title="Edit" onClick={() => openEditModal('templates', template)}>
                          <FaEdit />
                        </button>
                        <button 
                          className="btn-icon delete" 
                          title="Delete"
                          onClick={() => handleDeleteTemplate(template.id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <div className="template-body">
                      <div className="template-field">
                        <label>Subject:</label>
                        <p>{template.subject}</p>
                      </div>
                      <div className="template-field">
                        <label>Body:</label>
                        <div className="template-preview markdown-preview">
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]} 
                            rehypePlugins={[rehypeRaw]}
                          >
                            {template.body}
                          </ReactMarkdown>
                        </div>
                      </div>
                      {template.variables && (
                        <div className="template-variables">
                          <label>Available Variables:</label>
                          <div className="variables-list">
                            {(Array.isArray(template.variables) ? template.variables : JSON.parse(template.variables || '[]')).map((variable, idx) => (
                              <code key={idx} className="variable-tag">
                                {'{{' + variable + '}}'}
                              </code>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="template-footer">
                      <span className={`status-badge ${template.isActive ? 'active' : 'inactive'}`}>
                        {template.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Simplified Edit Modal */}
      {editingItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editingItem.id ? 'Edit' : 'Add'} {editingItem.type.replace('s', '').toUpperCase()}</h3>
            <form onSubmit={handleSave}>
              {editingItem.type === 'faqs' && (
                <>
                  <div className="form-group">
                    <label>Question</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingItem.data.question} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, question: e.target.value}})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Answer</label>
                    <textarea 
                      className="form-control" 
                      value={editingItem.data.answer} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, answer: e.target.value}})}
                      placeholder="Type your answer here. Supports:
- HTML (Copy/Paste from PDF)
- Markdown (| Table | Header |)"
                      required
                    />
                  </div>
                </>
              )}
              {editingItem.type === 'policies' && (
                <>
                  <div className="form-group">
                    <label>Title</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingItem.data.title} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, title: e.target.value}})}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Type</label>
                      <select 
                        className="form-control"
                        value={editingItem.data.type}
                        onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, type: e.target.value}})}
                        required
                      >
                        <option value="terms_of_service">Terms of Service</option>
                        <option value="privacy_policy">Privacy Policy</option>
                        <option value="cancellation_policy">Cancellation Policy</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Version</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={editingItem.data.version} 
                        onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, version: e.target.value}})}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Effective Date</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={editingItem.data.effectiveDate ? new Date(editingItem.data.effectiveDate).toISOString().split('T')[0] : ''} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, effectiveDate: e.target.value}})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Content</label>
                    <textarea 
                      className="form-control" 
                      value={editingItem.data.content} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, content: e.target.value}})}
                      placeholder="Paste your content here. Supports: HTML (Copy/Paste from PDF) and Markdown"
                      style={{ minHeight: '300px' }}
                      required
                    />
                  </div>
                </>
              )}
              {editingItem.type === 'articles' && (
                <>
                  <div className="form-group">
                    <label>Title</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingItem.data.title} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, title: e.target.value}})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Slug</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingItem.data.slug} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, slug: e.target.value}})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingItem.data.category} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, category: e.target.value}})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Content</label>
                    <textarea 
                      className="form-control" 
                      value={editingItem.data.content} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, content: e.target.value}})}
                      placeholder="Paste your content here. Supports: HTML (Copy/Paste from PDF) and Markdown"
                      style={{ minHeight: '300px' }}
                      required
                    />
                  </div>
                </>
              )}
              {editingItem.type === 'templates' && (
                <>
                  <div className="form-group">
                    <label>Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingItem.data.name} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, name: e.target.value}})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Subject</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingItem.data.subject} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, subject: e.target.value}})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Body</label>
                    <textarea 
                      className="form-control" 
                      value={editingItem.data.body} 
                      onChange={(e) => setEditingItem({...editingItem, data: {...editingItem.data, body: e.target.value}})}
                      required
                    />
                  </div>
                </>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingItem(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><FaSave /> Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {viewingItem && (
        <div className="modal-overlay" onClick={() => setViewingItem(null)}>
          <div className="modal-content preview-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{viewingItem.type} Preview: {viewingItem.title}</h3>
              <button className="btn-close" onClick={() => setViewingItem(null)}>&times;</button>
            </div>
            <div className="preview-body">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]} 
                rehypePlugins={[rehypeRaw]}
              >
                {viewingItem.content}
              </ReactMarkdown>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setViewingItem(null)}>Close Preview</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManagement;

